"use client";

import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import localspace, { quotaPlugin, ttlPlugin, type LocalSpaceOptions as TLocalSpaceOptions } from "localspace";
import { uniqueBy } from "~/shared/utils";
import { useEventCallback } from "./useEventCallback";
import { useEventListener } from "./useEventListener";
declare global {
  interface WindowEventMap {
    localspace: CustomEvent;
  }
}
interface LocalSpaceOptions extends TLocalSpaceOptions {
  /**
   * Whether to initialize the stored value on mount.
   *
   * @default true
   */
  readonly initializeWithValue?: boolean;
  /**
   * Time-to-live in milliseconds. Items older than this will be automatically removed on read.
   * Set to null or undefined for no expiration.
   *
   * @default 3600000 // 1 hour
   */
  readonly ttl?: number;
  /**
   * Interval in milliseconds to run expiry cleanup.
   *
   * @default 300000 // 5 minutes
   */
  readonly expiryCleanupInterval?: number;
}

/**
 * Indicates whether the current environment is a server.
 */
const IS_SERVER = typeof window === "undefined";

/**
 * Default time-to-live in milliseconds.
 *
 * @default 1 hour
 */
const DEFAULT_TTL = 1_000 * 60 * 60;

/**
 * Default expiry cleanup interval in milliseconds.
 *
 * @default 5 minutes
 */
const DEFAULT_EXPIRY_CLEANUP_INTERVAL = 5 * 60 * 1_000; // 5 minutes

/**
 * Maximum storage size for quota plugin.
 *
 * @default 5 MB
 */
const QUOTA_MAX_SIZE = 5 * 1024 * 1024; // 5 MB

/**
 * Default localspace database name.
 *
 * @default "vita"
 */
const DEFAULT_NAME = "vita";

/**
 * Default localspace store name.
 *
 * @default "localspace"
 */
const DEFAULT_STORE_NAME = "localspace";

/**
 * A React hook that synchronizes a stateful value with localspace storage.
 *
 * Localspace is a library that provides a unified API for various client-side storage
 * mechanisms, such as IndexedDB and LocalStorage.
 *
 * @param {string} key the key to use in localspace
 * @param {T | (() => T)} fallbackValue the value to use if there is nothing in localspace
 * @param {LocalSpaceOptions} options options for localspace
 * @returns {[T | null, Dispatch<SetStateAction<T | null>>, () => void} the stored value (null until loaded), a setter function, and a remover function
 */
export const useLocalSpace = <T,>(key: string, fallbackValue: (T | null) | (() => T | null), {
  initializeWithValue = true,
  ttl = DEFAULT_TTL,
  expiryCleanupInterval = DEFAULT_EXPIRY_CLEANUP_INTERVAL,
  plugins = [],
  ...options
}: LocalSpaceOptions = {
  initializeWithValue: true,
  ttl: DEFAULT_TTL,
  expiryCleanupInterval: DEFAULT_EXPIRY_CLEANUP_INTERVAL,
  plugins: []
}): [T | null, Dispatch<SetStateAction<T | null>>, () => void] => {
  /**
   * The localspace storage instance.
   */
  const storageRef = useRef(localspace.createInstance({
    name: DEFAULT_NAME,
    bucket: {
      name: DEFAULT_STORE_NAME,
      durability: "relaxed",
      persisted: true
    },
    storeName: DEFAULT_STORE_NAME,
    ...options,
    plugins: uniqueBy([ttlPlugin({
      defaultTTL: ttl,
      cleanupInterval: expiryCleanupInterval
    }), quotaPlugin({
      maxSize: QUOTA_MAX_SIZE,
      evictionPolicy: "lru"
    }), ...plugins], p => p.name)
  }));
  /**
   * Indicates whether the hook has been initialized at least once.
   */
  const isInitializedRef = useRef(false);
  /**
   * Indicates whether the storage is ready.
   * This prevents multiple calls to storageRef.current.ready().
   */
  const isReadyRef = useRef(false);

  /**
   * Reads the value from localspace.
   * @returns {Promise<T | null>} the stored value or the fallback value
   */
  const readValue = useCallback(async (): Promise<T | null> => {
    const initialValueToUse = fallbackValue instanceof Function ? fallbackValue() : fallbackValue;

    // Prevent build error "window is undefined" but keep working
    if (IS_SERVER) {
      return initialValueToUse;
    }
    try {
      // Wait for storage to be ready before reading
      if (!isReadyRef.current) {
        await storageRef.current.ready();
        isReadyRef.current = true;
      }

      // ttlPlugin handles expiry automatically - expired items return null
      const item = await storageRef.current.getItem<T>(key);
      return item !== null ? item : initialValueToUse;
    } catch (error) {
      console.warn(`Error reading localspace key "${key}":`, error);
      return initialValueToUse;
    }
  }, [fallbackValue, key]);

  // Initialize with null to indicate data hasn't loaded yet
  const [storedValue, setStoredValue] = useState<T | null>(null);

  /**
   * Sets the value in localspace.
   * @param {SetStateAction<T | null>} value the new value or a function that returns the new value
   */
  const setValue: Dispatch<SetStateAction<T | null>> = useEventCallback(value => {
    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      console.warn(`Tried setting localspace key "${key}" even though environment is not a client`);
      return;
    }
    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value;

      // Update state immediately for optimistic UI
      setStoredValue(newValue);

      // Save to storage asynchronously, waiting for ready state
      const persistToStorage = async () => {
        // Wait for storage to be ready
        if (!isReadyRef.current) {
          await storageRef.current.ready();
          isReadyRef.current = true;
        }

        // Handle null by removing the key
        if (newValue === null) {
          await storageRef.current.removeItem(key);
        } else {
          // ttlPlugin automatically applies TTL to stored values
          await storageRef.current.setItem(key, newValue);
        }
      };
      persistToStorage().catch(error_1 => {
        console.warn(`Error setting localspace key "${key}":`, error_1);
      });

      // We dispatch a custom event so every similar useLocalSpace hook is notified
      window.dispatchEvent(new CustomEvent("localspace", {
        detail: {
          key
        }
      }));
    } catch (error_0) {
      console.warn(`Error setting localspace key "${key}":`, error_0);
    }
  });

  /**
   * Removes the value from localspace.
   */
  const removeValue = useEventCallback(() => {
    // Prevent build error "window is undefined" but keeps working
    if (IS_SERVER) {
      console.warn(`Tried removing localspace key "${key}" even though environment is not a client`);
      return;
    }
    try {
      // Update state to null immediately
      setStoredValue(null);

      // Remove the key from storage asynchronously, waiting for ready state
      const removeFromStorage = async () => {
        // Wait for storage to be ready
        if (!isReadyRef.current) {
          await storageRef.current.ready();
          isReadyRef.current = true;
        }
        await storageRef.current.removeItem(key);
      };
      removeFromStorage().catch(error_3 => {
        console.warn(`Error removing localspace key "${key}":`, error_3);
      });

      // We dispatch a custom event so every similar useLocalSpace hook is notified
      window.dispatchEvent(new CustomEvent("localspace", {
        detail: {
          key
        }
      }));
    } catch (error_2) {
      console.warn(`Error removing localspace key "${key}":`, error_2);
    }
  });
  const handleStorageChange = useCallback((event: StorageEvent | CustomEvent) => {
    // Handle both StorageEvent (cross-tab) and CustomEvent (same-tab)
    const eventKey = (event as StorageEvent).key || (event as CustomEvent<{
      key: string;
    }>).detail?.key;
    if (eventKey && eventKey !== key) {
      return;
    }

    // Reload value from storage when storage changes
    readValue().then(setStoredValue).catch(error_4 => {
      console.warn(`Error handling storage change for key "${key}":`, error_4);
    });
  }, [key, readValue]);

  // Load value from storage on mount and when key changes
  useEffect(() => {
    if (!initializeWithValue && isInitializedRef.current) {
      return;
    }
    let cancelled = false;
    const loadValue = async () => {
      try {
        const value_0 = await readValue();
        if (!cancelled) {
          setStoredValue(value_0);
          isInitializedRef.current = true;
        }
      } catch (error_5) {
        console.warn(`Error loading localspace key "${key}":`, error_5);
      }
    };
    loadValue();
    return () => {
      cancelled = true;
    };
  }, [key, initializeWithValue, readValue]);

  // this only works for other documents, not the current one
  useEventListener("storage", handleStorageChange);

  // this is a custom event, triggered in setValue/removeValue
  // See: useLocalSpace()
  useEventListener("localspace", handleStorageChange);
  return [storedValue, setValue, removeValue];
};